import React, {useMemo} from 'react';
import {
    BrowseEventTrackingProvider,
    DiscoveryItemTypeOptions,
} from '@udemy-v2/react-browse-event-tracking';
import {CoursePriceProvider} from '@udemy-v2/react-course-pricing';
import {
    RecommendationSourceObjectType,
    RecommendationsProvider,
} from '@udemy-v2/react-recommendations';

// Maximum number of courses requested per recommendation unit returned for the
// limited-access surface.
const RECOMMENDATIONS_ITEM_COUNT_PER_UNIT = 12;

export type LimitedAccessRecommendationsContextId =
    | 'disabled_course_label'
    | 'disabled_course_cat';

export interface LimitedAccessRecommendationsSourceObject {
    id: string;
    type: RecommendationSourceObjectType;
}

export interface LimitedAccessRecommendationsWrapperProps {
    /**
     * Seed objects forwarded to the recommendations service. The accepted
     * `RecommendationSourceObjectType` varies based on the upstream surface:
     *  - topic            → RecommendationSourceObjectType.Label
     *  - category         → RecommendationSourceObjectType.Category
     *  - subcategory      → RecommendationSourceObjectType.Subcategory
     */
    sourceObjects: LimitedAccessRecommendationsSourceObject[];
    /** Recommendation context code, decided by the calling surface. */
    contextId: LimitedAccessRecommendationsContextId;
    children: React.ReactNode;
}

const SOURCE_TYPE_TO_DISCOVERY_TYPE: Partial<Record<RecommendationSourceObjectType, string>> = {
    [RecommendationSourceObjectType.Label]: DiscoveryItemTypeOptions.COURSE_TOPIC,
    [RecommendationSourceObjectType.Category]: DiscoveryItemTypeOptions.COURSE_CATEGORY,
    [RecommendationSourceObjectType.Subcategory]: DiscoveryItemTypeOptions.COURSE_SUBCATEGORY,
};

/**
 * Wraps the children with the RecommendationsProvider + BrowseEventTrackingProvider
 * used by the limited-access course screens (UnavailableCourse,
 * OrganizationCourseNotAvailable). Renders null when no seed objects are
 * supplied — without a seed there is nothing meaningful to recommend.
 */
export const LimitedAccessRecommendationsWrapper = ({
    sourceObjects,
    contextId,
    children,
}: LimitedAccessRecommendationsWrapperProps) => {
    const contextInput = useMemo(
        () => ({
            contextId,
            sourceObjects,
            requestedItemCountPerUnit: RECOMMENDATIONS_ITEM_COUNT_PER_UNIT,
        }),
        [contextId, sourceObjects],
    );

    const trackingContext = useMemo(() => {
        const first = sourceObjects[0];
        if (!first) return undefined;
        const relatedSourceType = SOURCE_TYPE_TO_DISCOVERY_TYPE[first.type];
        if (!relatedSourceType) return undefined;
        return {
            relatedSourceId: first.id,
            relatedSourceType,
        };
    }, [sourceObjects]);

    if (sourceObjects.length === 0) return null;

    return (
        <RecommendationsProvider
            contextInput={contextInput}
            includeAvailableSubscriptionInfo={true}
        >
            <BrowseEventTrackingProvider browseEventTrackingContext={trackingContext ?? {}}>
                <CoursePriceProvider>{children}</CoursePriceProvider>
            </BrowseEventTrackingProvider>
        </RecommendationsProvider>
    );
};
